import { useEffect, useState } from "react";
import { Pencil, Trash2, Plus, Check, X, Loader2 } from "lucide-react";
import FullScreenModal from "@/components/FullScreenModal";
import { formatCurrency } from "@vaulzo/core/constants";
import { EXPENSE_CATEGORIES, categoryLabel, recurringWhen } from "@vaulzo/core/domain/expenses";
import { kicker, fieldLabel, underline, RoundButton } from "./ui";

/** One editable row: the same form for a new rule and for an existing one. */
function EditorRow({ rule, onSave, onCancel, busy }) {
  const [name, setName] = useState(rule?.name || "");
  const [amount, setAmount] = useState(rule?.amount != null ? String(rule.amount) : "");
  const [day, setDay] = useState(rule?.day != null ? String(rule.day) : "");
  const [category, setCategory] = useState(rule?.category || "bills");
  const submit = (e) => {
    e.preventDefault();
    const a = parseFloat(amount);
    const d = Math.min(31, Math.max(1, parseInt(day, 10) || 1));
    if (!name.trim() || !(a > 0)) return;
    onSave({ name: name.trim(), amount: a, day: d, category });
  };
  return (
    <form onSubmit={submit} className="flex flex-wrap items-end gap-x-4 gap-y-3 px-4 pb-4 pt-3">
      <label className="block min-w-0 flex-[2_1_160px]">
        <span className={fieldLabel}>What is it?</span>
        <input value={name} onChange={(e) => setName(e.target.value)} autoFocus required maxLength={120} placeholder="e.g. Netflix, Gym" className={`${underline} text-[15px]`} />
      </label>
      <label className="block min-w-0 flex-[1_1_100px]">
        <span className={fieldLabel}>Amount</span>
        <div className="flex items-baseline gap-[6px] border-b-[1.5px] border-vaulzo-input-line focus-within:border-vaulzo-accent">
          <span className="text-vaulzo-fainter">₹</span>
          <input value={amount} onChange={(e) => setAmount(e.target.value)} type="number" step="0.01" min="0.01" required inputMode="decimal" placeholder="0" className="num w-full min-w-0 flex-1 border-0 bg-transparent py-2 text-[15px] text-vaulzo-ink outline-none placeholder:text-vaulzo-subtle" />
        </div>
      </label>
      <label className="block min-w-0 flex-[0_1_72px]">
        <span className={fieldLabel}>Day</span>
        <input value={day} onChange={(e) => setDay(e.target.value)} type="number" min="1" max="31" required title="31 means month end" placeholder="1" className={`num ${underline} text-[15px]`} />
      </label>
      <label className="block min-w-0 flex-[2_1_150px]">
        <span className={fieldLabel}>Category</span>
        <select value={category} onChange={(e) => setCategory(e.target.value)} className={`${underline} cursor-pointer appearance-none text-[15px]`}>
          {EXPENSE_CATEGORIES.map((c) => (
            <option key={c.key} value={c.key}>
              {c.label}
            </option>
          ))}
        </select>
      </label>
      <div className="ml-auto flex flex-none gap-[6px] pb-[2px]">
        <button type="button" onClick={onCancel} disabled={busy} aria-label="Cancel" className="flex h-9 w-9 items-center justify-center rounded-full bg-vaulzo-row text-vaulzo-muted transition-colors hover:bg-vaulzo-soft disabled:opacity-50">
          <X className="h-4 w-4" strokeWidth={2.2} />
        </button>
        <button type="submit" disabled={busy} aria-label="Save" className="flex h-9 w-9 items-center justify-center rounded-full bg-vaulzo-accent text-vaulzo-on-fill transition-colors hover:bg-vaulzo-accent-hover disabled:opacity-60">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> : <Check className="h-4 w-4" strokeWidth={2.2} />}
        </button>
      </div>
    </form>
  );
}

/**
 * Recurring expenses behind the gear: the rules, each editable in place,
 * and a plus that appends an empty editor row. One editor at a time.
 */
export default function RecurringModal({ open, onClose, rules, onSave, onDelete, saving = false }) {
  const [editing, setEditing] = useState(null); // null | "new" | rule id
  useEffect(() => {
    if (open) setEditing(null);
  }, [open]);
  if (!open) return null;
  const save = (id) => (payload) => onSave(payload, id, () => setEditing(null));

  return (
    <FullScreenModal open={open} onClose={onClose}>
      <div className="w-full max-w-[640px]">
        <div className={kicker}>Expenses</div>
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <h2 className="m-0 text-[34px] font-bold tracking-[-0.03em] text-vaulzo-ink">Recurring</h2>
            <div className="mt-2 text-sm leading-relaxed text-vaulzo-faint">Added to each month on its day. Edit a month's entry if the amount differs.</div>
          </div>
          <RoundButton tone="primary" label="Add recurring expense" onClick={() => setEditing("new")} disabled={editing !== null}>
            <Plus className="h-[18px] w-[18px]" strokeWidth={2.2} />
          </RoundButton>
        </div>

        <div className="mt-7 rounded-2xl border border-vaulzo-hairline bg-vaulzo-surface">
          {rules.length === 0 && editing !== "new" && <div className="py-10 text-center text-sm text-vaulzo-subtle">Nothing recurring yet.</div>}
          {rules.map((r, i) => (
            <div key={r.id} className={i ? "border-t border-vaulzo-row" : ""}>
              {editing === r.id ? (
                <EditorRow rule={r} busy={saving} onSave={save(r.id)} onCancel={() => setEditing(null)} />
              ) : (
                <div className="flex items-center gap-3 px-4 py-[14px]">
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-[15px] font-semibold text-vaulzo-ink">{r.name}</span>
                    <span className="num mt-[3px] block text-[12px] text-vaulzo-fainter">
                      {recurringWhen(r.day)} · {categoryLabel(r.category)}
                    </span>
                  </span>
                  <span className="num whitespace-nowrap text-[15px] font-semibold text-vaulzo-ink">{formatCurrency(r.amount)}</span>
                  <span className="flex flex-none gap-[2px]">
                    <button type="button" onClick={() => setEditing(r.id)} aria-label="Edit" className="flex items-center p-[6px] text-vaulzo-fainter transition-colors hover:text-vaulzo-accent">
                      <Pencil className="h-[15px] w-[15px]" strokeWidth={1.7} />
                    </button>
                    <button type="button" onClick={() => onDelete(r)} aria-label="Delete" className="flex items-center p-[6px] text-vaulzo-bar transition-colors hover:text-vaulzo-danger">
                      <Trash2 className="h-[15px] w-[15px]" strokeWidth={1.7} />
                    </button>
                  </span>
                </div>
              )}
            </div>
          ))}
          {editing === "new" && (
            <div className={rules.length ? "border-t border-vaulzo-row" : ""}>
              <EditorRow busy={saving} onSave={save(undefined)} onCancel={() => setEditing(null)} />
            </div>
          )}
        </div>
      </div>
    </FullScreenModal>
  );
}
